import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    Container,
    Input,
    Row,
    Col,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
} from "reactstrap";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import CreateTableComponent from "../../share/CreateTableComponent";
import { NumberWithCommmas2 } from "../../share/NumberWithCommmas2";
import "../../share/form.css";
import "../../share/modal.css";

export function Report62Component() {
    const currentYearTh = new Date().getFullYear() + 543;

    const [loading, setLoading] = useState(false);
    const [budgetYear, setBudgetYear] = useState(currentYearTh);
    const [data, setData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [selectedSupportTypeId, setSelectedSupportTypeId] = useState("");
    const [openModalForm, setOpenModalForm] = useState(false);
    const [modalRows, setModalRows] = useState([]);
    const [modalTitle, setModalTitle] = useState("");

    async function fetchYearOptions() {
        const res = await fetch("/api/Support/getyear57");
        const json = await res.json();
        setYearOptions(json.dataenum || []);
    }

    async function fetchData() {
        const res = await fetch("/api/SupportReports/dataReport62");
        const json = await res.json();
        setData(json.dataenum || []);
    }

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchYearOptions(), fetchData()]);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    const supportTypeOptions = useMemo(() => {
        const map = new Map();

        data.forEach((item) => {
            if (item.supportTypeId && item.supportTypeName) {
                const key = String(item.supportTypeId);

                if (!map.has(key)) {
                    map.set(key, {
                        supportTypeId: key,
                        supportTypeName: item.supportTypeName,
                    });
                }
            }
        });

        return Array.from(map.values()).sort(
            (a, b) => Number(a.supportTypeId) - Number(b.supportTypeId)
        );
    }, [data]);

    useEffect(() => {
        if (!selectedSupportTypeId && supportTypeOptions.length > 0) {
            setSelectedSupportTypeId(String(supportTypeOptions[0].supportTypeId));
        }
    }, [supportTypeOptions, selectedSupportTypeId]);

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const matchYear = String(item.budgetYear) === String(budgetYear);
            const matchSupportType = selectedSupportTypeId
                ? String(item.supportTypeId) === String(selectedSupportTypeId)
                : true;

            return matchYear && matchSupportType;
        });
    }, [data, budgetYear, selectedSupportTypeId]);

    const summaryRows = useMemo(() => {
        const grouped = new Map();

        filteredData.forEach((item) => {
            const key = item.subSupportReportTypeId || "unknown";

            if (!grouped.has(key)) {
                grouped.set(key, {
                    subSupportReportTypeId: item.subSupportReportTypeId,
                    name1: item.subSupportReportTypeName || "ไม่ระบุ",
                    amount: 0,
                    details: [],
                });
            }

            const target = grouped.get(key);
            target.amount += Number(item.amount || 0);
            target.details.push(item);
        });

        const rows = Array.from(grouped.values());
        const total = rows.reduce((sum, item) => sum + item.amount, 0);

        return rows
            .map((item) => ({
                ...item,
                percent: total > 0 ? ((item.amount / total) * 100).toFixed(2) : "0.00",
            }))
            .sort((a, b) => b.amount - a.amount);
    }, [filteredData]);

    const totalAmount = useMemo(() => {
        return summaryRows.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    }, [summaryRows]);

    const selectedSupportTypeName = useMemo(() => {
        const found = supportTypeOptions.find(
            (item) => String(item.supportTypeId) === String(selectedSupportTypeId)
        );
        return found?.supportTypeName || "-";
    }, [supportTypeOptions, selectedSupportTypeId]);

    const modalToggle = () => {
        setOpenModalForm((prev) => !prev);
    };

    const openDetailModal = (row) => {
        setModalTitle(row.name1 || "รายละเอียด");
        setModalRows(row.details || []);
        setOpenModalForm(true);
    };

    const exportExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];

        wsData.push(["รายงานรายละเอียดข้อมูลสนับสนุน"]);
        wsData.push([`ปีงบประมาณ ${budgetYear}`]);
        wsData.push([`ประเภท: ${selectedSupportTypeName}`]);
        wsData.push([]);

        wsData.push([
            "ลำดับ",
            "ชื่อผู้ได้รับเงินช่วยเหลือหรืออุดหนุน",
            "หน่วยงาน",
            "วันที่อนุมัติ",
            "จำนวนเงิน",
        ]);

        filteredData.forEach((row, index) => {
            wsData.push([
                index + 1,
                row.supportName || "",
                row.deptName || "",
                row.approveDateSt || "",
                Number(row.amount || 0),
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws["!cols"] = [
            { wch: 10 },
            { wch: 55 },
            { wch: 36 },
            { wch: 18 },
            { wch: 18 },
        ];

        ws["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        ];

        XLSX.utils.book_append_sheet(wb, ws, "Report62_Detail");
        XLSX.writeFile(wb, `Report62_Detail_${budgetYear}.xlsx`);
    };

    const COLORS = [
        "#5B5FEF",
        "#7277F5",
        "#8B91F8",
        "#A7ACFB",
        "#7DC8F7",
        "#65D7E7",
        "#98E4C8",
        "#C8B8F8",
    ];

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;

        const item = payload[0].payload;

        return (
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e7edf5",
                    borderRadius: 14,
                    padding: "10px 12px",
                    boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
                    fontSize: 13,
                }}
            >
                <div style={{ fontWeight: 700, marginBottom: 4, color: "#1f2937" }}>
                    {item.name1}
                </div>
                <div style={{ color: "#4b5563" }}>
                    จำนวน: {NumberWithCommmas2(item.amount)} แห่ง
                </div>
                <div style={{ color: "#4b5563" }}>
                    ร้อยละ: {item.percent}%
                </div>
            </div>
        );
    };

    const DETAIL_COLUMNS = [
        {
            Header: "ลำดับ",
            accessor: (_row, i) => i + 1,
        },
        {
            Header: "ชื่อผู้ได้รับเงินช่วยเหลือหรืออุดหนุน",
            accessor: "supportName",
        },
        {
            Header: "หน่วยงาน",
            accessor: "deptName",
        },
        {
            Header: "วันที่อนุมัติ",
            accessor: "approveDateSt",
        },
        {
            Header: "จำนวนเงิน",
            accessor: "approveAmount",
            Cell: ({ value }) => {
                return <div className="text-right">{NumberWithCommmas2(value || 0)}</div>
            },
        },
    ];

    return (
        <Container fluid className="py-4 px-3 px-xl-4">
            <div
                style={{
                    width: "100%",
                    maxWidth: "min(1480px, 96vw)",
                    margin: "0 auto",
                }}
            >
                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #e8eef6",
                        borderRadius: 18,
                        padding: 22,
                        marginBottom: 18,
                        boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                    }}
                >

                    <Row className="g-3">
                        <Col lg={6} md={6} sm={12}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: 8,
                                    fontWeight: 600,
                                    color: "#374151",
                                }}
                            >
                                ปีงบประมาณ
                            </label>
                            <Input
                                type="select"
                                value={budgetYear}
                                disabled={loading}
                                onChange={(e) => setBudgetYear(e.target.value)}
                                style={{
                                    borderRadius: 12,
                                    minHeight: 46,
                                    borderColor: "#d9e2ec",
                                    fontSize: 14,
                                }}
                            >
                                {yearOptions.map((y) => (
                                    <option key={y.yearTh} value={y.yearTh}>
                                        {y.yearTh}
                                    </option>
                                ))}
                            </Input>
                        </Col>

                        <Col lg={6} md={6} sm={12}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: 8,
                                    fontWeight: 600,
                                    color: "#374151",
                                }}
                            >
                                ประกาศ / ประเภทผู้ขอ
                            </label>
                            <Input
                                type="select"
                                value={selectedSupportTypeId}
                                disabled={loading}
                                onChange={(e) => setSelectedSupportTypeId(e.target.value)}
                                style={{
                                    borderRadius: 12,
                                    minHeight: 46,
                                    borderColor: "#d9e2ec",
                                    fontSize: 14,
                                }}
                            >
                                {supportTypeOptions.map((item) => (
                                    <option key={item.supportTypeId} value={item.supportTypeId}>
                                        {item.supportTypeName}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                </div>

                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #e8eef6",
                        borderRadius: 18,
                        padding: 24,
                        marginBottom: 20,
                        boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                    }}
                >
                    <div style={{ width: "100%", height: 500 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={summaryRows}
                                    dataKey="amount"
                                    nameKey="name1"
                                    cx="50%"
                                    cy="46%"
                                    innerRadius={90}
                                    outerRadius={165}
                                    paddingAngle={4}
                                    cornerRadius={10}
                                    stroke="#ffffff"
                                    strokeWidth={4}
                                >
                                    {summaryRows.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={CustomTooltip} />
                                <Legend
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{
                                        fontSize: "13px",
                                        paddingTop: "14px",
                                        lineHeight: 1.8,
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 12,
                    }}
                >
                    <div
                        style={{
                            fontWeight: 700,
                            color: "#1f2937",
                            fontSize: 18,
                        }}
                    >
                        ตารางสรุป
                    </div>

                    <Button
                        color="success"
                        onClick={exportExcel}
                        disabled={loading || !filteredData.length}
                        style={{
                            borderRadius: 10,
                            padding: "8px 16px",
                            fontWeight: 600,
                            boxShadow: "0 8px 18px rgba(34,197,94,0.18)",
                        }}
                    >
                        Export รายชื่อ
                    </Button>
                </div>

                {loading ? (
                    <div
                        style={{
                            background: "#fff",
                            border: "1px solid #e8eef6",
                            borderRadius: 18,
                            padding: 40,
                            textAlign: "center",
                        }}
                    >
                        <div className="spinner-border text-primary" />
                        <div className="mt-2">กำลังโหลดข้อมูล...</div>
                    </div>
                ) : (
                    <div
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e8eef6",
                            borderRadius: 18,
                            overflow: "hidden",
                            boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                        }}
                    >
                        <Table
                            bordered={false}
                            hover
                            responsive
                            className="mb-0"
                            style={{ marginBottom: 0 }}
                        >
                            <thead style={{ background: "#f8fafc" }}>
                                <tr>
                                    <th
                                        className="text-center"
                                        style={{
                                            width: 70,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "#374151",
                                            padding: "14px 12px",
                                        }}
                                    >
                                        ที่
                                    </th>
                                    <th
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "#374151",
                                            padding: "14px 12px",
                                        }}
                                    >
                                        รายการ
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{
                                            width: 150,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "#374151",
                                            padding: "14px 12px",
                                        }}
                                    >
                                        จำนวน (แห่ง)
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{
                                            width: 110,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "#374151",
                                            padding: "14px 12px",
                                        }}
                                    >
                                        ร้อยละ
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{
                                            width: 110,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "#374151",
                                            padding: "14px 12px",
                                        }}
                                    >
                                        ดูรายละเอียด
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {summaryRows.length > 0 ? (
                                    summaryRows.map((row, index) => (
                                        <tr key={`${row.subSupportReportTypeId}-${index}`}>
                                            <td
                                                className="text-center"
                                                style={{
                                                    padding: "12px 12px",
                                                    verticalAlign: "middle",
                                                    fontSize: 14,
                                                }}
                                            >
                                                {index + 1}
                                            </td>
                                            <td
                                                style={{
                                                    minWidth: 760,
                                                    padding: "12px 12px",
                                                    verticalAlign: "middle",
                                                    fontSize: 14,
                                                    color: "#1f2937",
                                                }}
                                            >
                                                {row.name1}
                                            </td>
                                            <td
                                                className="text-center"
                                                style={{
                                                    padding: "12px 12px",
                                                    verticalAlign: "middle",
                                                    fontWeight: 600,
                                                    color: "#111827",
                                                }}
                                            >
                                                {NumberWithCommmas2(row.amount)}
                                            </td>
                                            <td
                                                className="text-center"
                                                style={{
                                                    padding: "12px 12px",
                                                    verticalAlign: "middle",
                                                    color: "#374151",
                                                }}
                                            >
                                                {row.percent}%
                                            </td>
                                            <td
                                                className="text-center"
                                                style={{
                                                    padding: "12px 12px",
                                                    verticalAlign: "middle",
                                                }}
                                            >
                                                <Button
                                                    color="light"
                                                    onClick={() => openDetailModal(row)}
                                                    style={{
                                                        width: 38,
                                                        height: 38,
                                                        borderRadius: 999,
                                                        border: "1px solid #dbe4f0",
                                                        background: "#f8fafc",
                                                        padding: 0,
                                                        fontSize: 16,
                                                        boxShadow: "0 4px 10px rgba(15,23,42,0.05)",
                                                    }}
                                                    title="ดูรายละเอียด"
                                                >
                                                    🔍
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center py-4 text-muted"
                                        >
                                            ไม่พบข้อมูล
                                        </td>
                                    </tr>
                                )}

                                {summaryRows.length > 0 && (
                                    <tr style={{ background: "#f8fafc" }}>
                                        <td
                                            colSpan="2"
                                            className="text-end"
                                            style={{
                                                fontWeight: 700,
                                                padding: "13px 12px",
                                            }}
                                        >
                                            รวม
                                        </td>
                                        <td
                                            className="text-center"
                                            style={{
                                                fontWeight: 700,
                                                padding: "13px 12px",
                                            }}
                                        >
                                            {NumberWithCommmas2(totalAmount)}
                                        </td>
                                        <td
                                            className="text-center"
                                            style={{
                                                fontWeight: 700,
                                                padding: "13px 12px",
                                            }}
                                        >
                                            100%
                                        </td>
                                        <td />
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>

            <Modal
                isOpen={openModalForm}
                toggle={modalToggle}
                centered
                scrollable
                backdrop="static"
                keyboard={false}
                size="xl"
                style={{ maxWidth: "1400px", width: "96%" }}
            >
                <ModalHeader
                    toggle={modalToggle}
                    style={{
                        borderBottom: "1px solid #eef2f7",
                        fontWeight: 700,
                        fontSize: "1rem",
                        padding: "16px 20px",
                        color: "#1f2937",
                    }}
                >
                    รายละเอียดข้อมูล : {modalTitle}
                </ModalHeader>

                <ModalBody
                    style={{
                        padding: "16px 18px 18px",
                        background: "#fbfcfe",
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            border: "1px solid #e8eef6",
                            borderRadius: 14,
                            overflow: "hidden",
                            boxShadow: "0 6px 20px rgba(15,23,42,0.04)",
                        }}
                    >
                        <div
                            style={{
                                padding: "14px 16px",
                                borderBottom: "1px solid #eef2f7",
                                background: "#f8fafc",
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 700,
                                    color: "#1f2937",
                                    fontSize: 14,
                                }}
                            >
                                รายชื่อผู้ได้รับเงินช่วยเหลือหรืออุดหนุน
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#6b7280",
                                    marginTop: 2,
                                }}
                            >
                                จำนวน {modalRows.length} รายการ
                            </div>
                        </div>

                        <div style={{ padding: 10 }}>
                            <CreateTableComponent
                                column={DETAIL_COLUMNS}
                                dataInput={modalRows}
                                showEntrie={true}
                                showPage={true}
                            />
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
}